import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Lock, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";

/**
 * AuthGate — vriendelijke soft-gate voor pagina's die login vereisen.
 * Toont een nette landingssectie met login + gratis registratie CTA
 * en bewaart de bedoelde route in ?next=... zodat we na login terugsturen.
 */
export default function AuthGate({ children, title, subtitle, bullets }) {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  if (loading) return <div className="max-w-3xl mx-auto py-24 px-4 text-center text-slate-500" data-testid="authgate-loading">{t("loading")}</div>;
  if (user) return children;

  const next = encodeURIComponent(location.pathname + location.search);
  const defaultBullets = [
    t("authgate.bullet1"),
    t("authgate.bullet2"),
    t("authgate.bullet3"),
    t("authgate.bullet4"),
  ];
  const items = bullets && bullets.length ? bullets : defaultBullets;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24" data-testid="authgate">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--wv-navy)]/5 text-[color:var(--wv-navy)] text-xs font-bold uppercase tracking-widest">
            <Lock className="w-3.5 h-3.5" /> {t("authgate.badge")}
          </div>
          <h1 className="mt-5 font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] leading-[1.1]">
            {title || t("authgate.title")}
          </h1>
          <p className="mt-4 text-slate-600 max-w-lg text-lg">
            {subtitle || t("authgate.subtitle")}
          </p>

          <ul className="mt-6 space-y-2">
            {items.map((b) => (
              <li key={b} className="flex items-start gap-2 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-[color:var(--wv-green)] mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/register?next=${next}`} className="wv-btn-green inline-flex items-center gap-2" data-testid="authgate-register">
              <Sparkles className="w-4 h-4" /> {t("cta.register_free")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={`/login?next=${next}`} className="wv-btn-outline inline-flex items-center gap-2" data-testid="authgate-login">
              {t("cta.have_account")}
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            {t("authgate.terms_prefix")} <Link to="/algemene-voorwaarden" className="hover:underline">{t("authgate.terms")}</Link> {t("authgate.and")} <Link to="/privacy" className="hover:underline">{t("authgate.privacy")}</Link>.
          </p>
        </div>

        <div className="relative">
          <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[420px] sm:h-[500px]">
            <img src="https://images.unsplash.com/photo-1754553080373-3a806029796a?crop=entropy&cs=srgb&fm=jpg&q=85"
                 alt="Amsterdam" className="col-span-4 row-span-4 object-cover w-full h-full rounded-3xl shadow-xl" />
            <div className="col-span-2 row-span-3 rounded-2xl bg-[color:var(--wv-navy)] text-white p-4 flex flex-col justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-white/60">{t("authgate.match_label")}</p>
              <div>
                <p className="font-display text-4xl font-extrabold text-[color:var(--wv-green)] font-mono-num">96%</p>
                <p className="text-xs text-white/80 mt-1">{t("authgate.match_desc")}</p>
              </div>
            </div>
            <img src="https://images.unsplash.com/photo-1760072513367-55182245e76c?crop=entropy&cs=srgb&fm=jpg&q=85"
                 alt="Interior" className="col-span-3 row-span-2 object-cover w-full h-full rounded-2xl shadow-md" />
            <img src="https://images.unsplash.com/photo-1701422052273-fd673aed2d1c?crop=entropy&cs=srgb&fm=jpg&q=85"
                 alt="Living" className="col-span-3 row-span-2 object-cover w-full h-full rounded-2xl shadow-md" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function GateOr({ redirect, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10">Laden…</div>;
  if (!user) return <Navigate to={redirect || "/login"} replace />;
  return children;
}
